import React from "react";
import { useNavigate } from "react-router-dom";
import { FaUserPlus, FaCheck, FaTimes, FaQuoteLeft, FaUsers, FaUserFriends, FaExternalLinkAlt } from "react-icons/fa";
import { getProfilePicture } from "../utils/avatar";

/**
 * Rich profile overlay card shown when clicking an agent on the Community Map.
 *
 * context="onboarding" (default): Shows Follow/Following button
 * context="app": Shows "View Full Profile" button linking to profile page
 */
const CommunityProfileCard = ({ user, onFollow, onClose, stanceLabels, context = "onboarding" }) => {
  const navigate = useNavigate();

  if (!user) return null;

  const proLabel = stanceLabels?.pro || "Supports this";
  const contraLabel = stanceLabels?.contra || "Opposes this";
  const isPro = user.stance === "pro";
  const stanceInfo = isPro
    ? { label: proLabel, bgClass: "bg-spiders-accent-500/15", textClass: "text-spiders-accent-400", borderClass: "border-spiders-accent-500/30", icon: "👍" }
    : { label: contraLabel, bgClass: "bg-spiders-primary-500/15", textClass: "text-spiders-primary-400", borderClass: "border-spiders-primary-500/30", icon: "👎" };

  const handleViewProfile = () => {
    onClose();
    navigate(`/profile/${user.username}`);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-spiders-bg-surface border border-spiders-border rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Colored header banner — taller for more visual presence */}
        <div className={`h-24 ${isPro ? "bg-gradient-to-br from-spiders-accent-500/25 via-spiders-accent-500/10 to-transparent" : "bg-gradient-to-br from-spiders-primary-500/25 via-spiders-primary-500/10 to-transparent"}`} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-spiders-text-muted hover:text-spiders-text-primary transition-colors p-1.5 rounded-full hover:bg-spiders-bg-elevated/80"
        >
          <FaTimes className="w-4 h-4" />
        </button>

        {/* Avatar — overlapping the banner */}
        <div className="flex justify-center -mt-14">
          <img
            src={getProfilePicture(user.username, user.avatarSeed)}
            alt={user.username}
            className={`w-28 h-28 rounded-full border-4 ${
              isPro ? "border-spiders-accent-400" : "border-spiders-primary-400"
            } shadow-xl bg-spiders-bg-surface`}
          />
        </div>

        {/* Name + stance */}
        <div className="text-center px-6 pt-3 pb-1">
          <h3 className="text-xl font-bold text-spiders-text-primary">
            @{user.username}
          </h3>
          <div className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full mt-2 border ${stanceInfo.bgClass} ${stanceInfo.textClass} ${stanceInfo.borderClass}`}>
            {stanceInfo.icon} {stanceInfo.label}
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-center gap-8 px-6 py-3">
          <div className="text-center">
            <span className="block font-bold text-spiders-text-primary text-lg">{user.followerCount || 0}</span>
            <span className="text-spiders-text-muted text-xs">followers</span>
          </div>
          <div className="w-px h-8 bg-spiders-border/50" />
          <div className="text-center">
            <span className="block font-bold text-spiders-text-primary text-lg">{user.followingCount || 0}</span>
            <span className="text-spiders-text-muted text-xs">following</span>
          </div>
        </div>

        {/* Bio */}
        {user.bio && (
          <div className="px-6 pb-3">
            <div className="bg-spiders-bg-elevated/50 rounded-xl p-4">
              <p className="text-sm text-spiders-text-secondary leading-relaxed">
                {user.bio}
              </p>
            </div>
          </div>
        )}

        {/* Sample message — full text, scrollable if long */}
        {user.sampleMessage && (
          <div className="px-6 pb-4">
            <p className="text-[10px] text-spiders-text-muted uppercase tracking-wider mb-2 font-medium">Recent post</p>
            <div
              className={`rounded-xl p-4 ${isPro ? "border-l-spiders-accent-500/50 bg-spiders-accent-500/5" : "border-l-spiders-primary-500/50 bg-spiders-primary-500/5"}`}
              style={{ borderLeftWidth: "3px", maxHeight: "150px", overflowY: "auto" }}
            >
              <div className="flex gap-3">
                <FaQuoteLeft className={`w-4 h-4 mt-0.5 flex-shrink-0 ${stanceInfo.textClass} opacity-40`} />
                <p className="text-sm text-spiders-text-secondary leading-relaxed italic">
                  {user.sampleMessage}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action button — context-dependent */}
        <div className="px-6 pb-6">
          {context === "app" ? (
            <button
              onClick={handleViewProfile}
              className="w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 bg-spiders-bg-elevated text-spiders-text-primary hover:bg-spiders-bg-light border border-spiders-border hover:border-spiders-text-muted"
            >
              <FaExternalLinkAlt className="w-3.5 h-3.5" />
              View Full Profile
            </button>
          ) : (
            <button
              onClick={() => onFollow(user._id)}
              className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                user.isFollowedByMe
                  ? isPro
                    ? "bg-spiders-accent-500 text-white shadow-lg shadow-spiders-accent-500/20"
                    : "bg-spiders-primary-500 text-white shadow-lg shadow-spiders-primary-500/20"
                  : "bg-spiders-bg-elevated text-spiders-text-secondary hover:bg-spiders-bg-light border border-spiders-border hover:border-spiders-text-muted"
              }`}
            >
              {user.isFollowedByMe ? (
                <><FaCheck className="w-4 h-4" /> Following</>
              ) : (
                <><FaUserPlus className="w-4 h-4" /> Follow</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityProfileCard;
